import { inject, Injectable } from '@angular/core';
import { ErrorHandlingService } from '../../core/services/error-handling.service';
import {
  DataRefetchService,
  QueryParams,
  PaginatedData,
} from '../../core/services/data-refetch.service';
import { environment } from '../../../environments/environment.development';
import { HttpClient } from '@angular/common/http';
import {
  BehaviorSubject,
  Observable,
  catchError,
  tap,
  filter,
  switchMap,
  of,
  Subject,
  finalize,
  map,
} from 'rxjs';
import {
  ApiResponse,
  Broadcast,
  BroadcastNotification,
  BroadcastRequest,
  PaginatedResult,
} from './broadcast';
import { AppService } from '../../core/services/app.service';
import { TimeZoneService } from '../../core/services/timezone.service';

@Injectable({
  providedIn: 'root',
})
export class BroadcastService {
  private readonly errorHandler = inject(ErrorHandlingService);
  private readonly dataRefetchService = inject(DataRefetchService);
  private readonly http = inject(HttpClient);
  private readonly appService = inject(AppService);

  private readonly BASE_URL = environment.apiUrl;
  private readonly BROADCAST_URL = `${this.BASE_URL}/broadcastnotification`;

  private broadcastsSubject = new BehaviorSubject<ApiResponse<
    PaginatedResult<Broadcast>
  > | null>(null);
  private broadcastNotificationsSubject = new BehaviorSubject<ApiResponse<
    BroadcastNotification[]
  > | null>(null);
  private alertsSubject = new BehaviorSubject<Broadcast[]>([]);
  private shownBroadcastVersions = new Map<number, string>();

  private isFetchingBroadcasts = false;
  private isFetchingNotifications = false;
  private currentOrganizationId: number | null = null;

  private refreshBroadcasts$ = new Subject<number | null>();

  broadcasts$ = this.broadcastsSubject.asObservable();
  broadcastNotifications$ = this.broadcastNotificationsSubject.asObservable();
  alerts$ = this.alertsSubject.asObservable();

  private lastQueryParams: QueryParams = {
    pageNumber: 1,
    pageSize: 10,
  };

  constructor(private timeZoneService: TimeZoneService) {
    this.setupSignalRNotificationHandler();
    this.refreshBroadcasts$
      .pipe(
        filter(() => !this.isFetchingBroadcasts),
        switchMap(orgId => {
          return this.fetchBroadcasts(
            orgId || undefined,
            this.lastQueryParams.pageNumber || 1,
            this.lastQueryParams.pageSize || 10,
            this.lastQueryParams.sortField,
            this.lastQueryParams.sortOrder,
            this.lastQueryParams.searchTerm
          );
        })
      )
      .subscribe();
  }

  private setupSignalRNotificationHandler(): void {
    this.appService.notificationReceived$
      .pipe(
        filter(notification => !!notification && !!notification.body),
        switchMap(notification => {
          try {
            if (!this.isValidJson(notification.body)) {
              return of(null);
            }

            const notificationData = JSON.parse(notification.body);
            if (
              notificationData.IsBroadcast === true &&
              notificationData.OrgnizationIds &&
              notificationData.OrgnizationIds.length > 0
            ) {
              return of({
                notification,
                broadcastData: notificationData,
              });
            }
            return of(null);
          } catch (error) {
            console.error('Error processing broadcast notification:', error);
            return of(null);
          }
        }),
        filter(data => !!data)
      )
      .subscribe(data => {
        if (data) {
          if (this.currentOrganizationId) {
            if (
              data.broadcastData.OrgnizationIds.includes(
                this.currentOrganizationId.toString()
              )
            ) {
              this.refreshBroadcasts(this.currentOrganizationId);
            }
          } else {
            this.refreshBroadcasts(null);
          }
        }
      });
  }

  getBroadcasts(
    organizationId?: number,
    pageNumber = 1,
    pageSize = 10,
    sortField?: string | null,
    sortOrder?: string | null,
    searchTerm?: string | null
  ): Observable<ApiResponse<PaginatedResult<Broadcast>>> {
    const currentOrgId = this.lastQueryParams?.organizationId;
    const orgChanged = currentOrgId !== organizationId;

    if (this.isFetchingBroadcasts && !orgChanged) {
      return this.broadcastsSubject.getValue()
        ? of(this.broadcastsSubject.getValue()!)
        : this.fetchBroadcasts(
            organizationId,
            pageNumber,
            pageSize,
            sortField,
            sortOrder,
            searchTerm
          );
    }

    return this.fetchBroadcasts(
      organizationId,
      pageNumber,
      pageSize,
      sortField,
      sortOrder,
      searchTerm
    );
  }

  private fetchBroadcasts(
    organizationId?: number,
    pageNumber = 1,
    pageSize = 10,
    sortField?: string | null,
    sortOrder?: string | null,
    searchTerm?: string | null
  ): Observable<ApiResponse<PaginatedResult<Broadcast>>> {
    this.lastQueryParams = {
      organizationId,
      pageNumber,
      pageSize,
      sortField,
      sortOrder,
      searchTerm,
    };

    const params = this.dataRefetchService.buildHttpParams(
      this.lastQueryParams
    );
    this.isFetchingBroadcasts = true;

    this.alertsSubject.next([]);
    return this.http
      .get<
        ApiResponse<PaginatedResult<Broadcast>>
      >(this.BROADCAST_URL, { params })
      .pipe(
        map(response => {
          if (response.result?.items) {
            response.result.items = response.result.items.map(broadcast => ({
              ...broadcast,
              startDateTime: broadcast.startDateTime
                ? (this.timeZoneService
                    .convertToUserTimeZone(broadcast.startDateTime)
                    ?.toISOString() ?? broadcast.startDateTime)
                : broadcast.startDateTime,
              endDateTime: broadcast.endDateTime
                ? (this.timeZoneService
                    .convertToUserTimeZone(broadcast.endDateTime)
                    ?.toISOString() ?? broadcast.endDateTime)
                : broadcast.endDateTime,
            }));
          }

          return response;
        }),
        tap(response => {
          this.broadcastsSubject.next(response);
          this.shownBroadcastVersions.clear();
          this.updateAlerts(response.result.items || []);
        }),
        catchError(error => {
          console.error('Error fetching broadcasts:', error);
          return this.errorHandler.handleError(error);
        }),
        finalize(() => {
          this.isFetchingBroadcasts = false;
        })
      );
  }

  clearShownBroadcasts(): void {
    this.shownBroadcastVersions.clear();
  }

  clearBroadcasts(): void {
    this.broadcastsSubject.next(null);
    this.alertsSubject.next([]);
  }

  /**
   * Create a new broadcast
   */
  createBroadcast(
    broadcastRequest: BroadcastRequest
  ): Observable<ApiResponse<string>> {
    return this.dataRefetchService.handleApiWithRefetch(
      this.http.post<ApiResponse<string>>(this.BROADCAST_URL, broadcastRequest),
      params => this.refetchBroadcasts(params),
      this.lastQueryParams
    );
  }

  getBroadcastNotificationsByOrgId(
    organizationId: number | null
  ): Observable<ApiResponse<BroadcastNotification[]>> {
    if (this.isFetchingNotifications) {
      return this.broadcastNotificationsSubject.getValue()
        ? of(this.broadcastNotificationsSubject.getValue()!)
        : this.fetchBroadcastNotifications(organizationId);
    }

    return this.fetchBroadcastNotifications(organizationId);
  }

  private fetchBroadcastNotifications(
    organizationId: number | null
  ): Observable<ApiResponse<BroadcastNotification[]>> {
    this.isFetchingNotifications = true;

    let url = `${this.BROADCAST_URL}/get-broadcastnotification-by-org-id`;
    if (organizationId !== null && organizationId !== undefined) {
      url += `?organizationId=${organizationId}`;
    }

    return this.http.get<ApiResponse<BroadcastNotification[]>>(url).pipe(
      tap(response => {
        this.broadcastNotificationsSubject.next(response);
      }),
      catchError(error => this.errorHandler.handleError(error)),
      finalize(() => {
        this.isFetchingNotifications = false;
      })
    );
  }

  getBroadcastById(id: number): Observable<ApiResponse<Broadcast>> {
    return this.http
      .get<ApiResponse<Broadcast>>(`${this.BROADCAST_URL}/${id}`)
      .pipe(
        map(response => {
          if (response.result) {
            response.result = {
              ...response.result,
              startDateTime: response.result.startDateTime
                ? (this.timeZoneService
                    .convertToUserTimeZone(response.result.startDateTime)
                    ?.toISOString() ?? '')
                : '',
              endDateTime: response.result.endDateTime
                ? (this.timeZoneService
                    .convertToUserTimeZone(response.result.endDateTime)
                    ?.toISOString() ?? '')
                : '',
            };
          }
          return response;
        }),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  /**
   * Update a broadcast
   */
  updateBroadcast(
    id: number,
    broadcastRequest: BroadcastRequest
  ): Observable<ApiResponse<string>> {
    return this.dataRefetchService.handleApiWithRefetch(
      this.http.put<ApiResponse<string>>(
        `${this.BROADCAST_URL}/${id}`,
        broadcastRequest
      ),
      params => this.refetchBroadcasts(params),
      this.lastQueryParams
    );
  }

  deleteBroadcast(id: number): Observable<ApiResponse<string>> {
    const currentData = this.broadcastsSubject.getValue()?.result || null;

    return this.dataRefetchService.handleApiWithRefetch(
      this.http.delete<ApiResponse<string>>(`${this.BROADCAST_URL}/${id}`),
      params => this.refetchBroadcasts(params),
      this.lastQueryParams,
      params =>
        this.dataRefetchService.calculatePageAfterDelete(
          params,
          currentData as PaginatedData
        )
    );
  }

  private refetchBroadcasts(
    params: QueryParams
  ): Observable<ApiResponse<PaginatedResult<Broadcast>>> {
    return this.fetchBroadcasts(
      params.organizationId,
      params.pageNumber || 1,
      params.pageSize || 10,
      params.sortField,
      params.sortOrder,
      params.searchTerm
    );
  }

  refreshBroadcasts(organizationId: number | null): void {
    if (!this.isFetchingBroadcasts) {
      this.refreshBroadcasts$.next(organizationId);
    }
  }

  fetchBroadcastData(
    organizationId: number
  ): Observable<ApiResponse<PaginatedResult<Broadcast>>> {
    if (this.isFetchingBroadcasts || this.isFetchingNotifications) {
      return this.broadcastsSubject.getValue()
        ? of(this.broadcastsSubject.getValue()!)
        : of(null as unknown as ApiResponse<PaginatedResult<Broadcast>>);
    }

    return this.fetchBroadcastNotifications(organizationId).pipe(
      switchMap(() =>
        this.fetchBroadcasts(
          organizationId,
          this.lastQueryParams.pageNumber,
          this.lastQueryParams.pageSize,
          this.lastQueryParams.sortField,
          this.lastQueryParams.sortOrder,
          this.lastQueryParams.searchTerm
        )
      )
    );
  }

  private updateAlerts(broadcasts: Broadcast[]): void {
    const now = new Date();
    const activeAlerts = broadcasts.filter(
      broadcast =>
        broadcast.broadcastTypeId === 105 &&
        !broadcast.isDraft &&
        broadcast.startDateTime &&
        new Date(broadcast.startDateTime) <= now &&
        (!broadcast.endDateTime || new Date(broadcast.endDateTime) >= now)
    );

    this.alertsSubject.next(activeAlerts);
  }

  private getBroadcastVersion(broadcast: Broadcast): string {
    return JSON.stringify({
      title: broadcast.title,
      message: broadcast.message,
      startDateTime: broadcast.startDateTime,
      endDateTime: broadcast.endDateTime,
      broadcastTypeId: broadcast.broadcastTypeId,
      isDraft: broadcast.isDraft,
    });
  }

  markBroadcastAsShown(broadcastId: number, broadcast?: Broadcast): void {
    if (broadcast) {
      const version = this.getBroadcastVersion(broadcast);
      this.shownBroadcastVersions.set(broadcastId, version);
    } else {
      const currentBroadcasts =
        this.broadcastsSubject.getValue()?.result?.items || [];
      const foundBroadcast = currentBroadcasts.find(b => b.id === broadcastId);

      if (foundBroadcast) {
        const version = this.getBroadcastVersion(foundBroadcast);
        this.shownBroadcastVersions.set(broadcastId, version);
      }
    }
  }

  markBroadcastsAsShown(broadcastIds: number[]): void {
    const currentBroadcasts =
      this.broadcastsSubject.getValue()?.result?.items || [];

    broadcastIds.forEach(id => {
      const broadcast = currentBroadcasts.find(b => b.id === id);
      if (broadcast) {
        this.markBroadcastAsShown(id, broadcast);
      }
    });
  }

  hasBeenShown(broadcast: Broadcast): boolean {
    const currentVersion = this.getBroadcastVersion(broadcast);
    const shownVersion = this.shownBroadcastVersions.get(broadcast.id);
    return shownVersion === currentVersion;
  }

  hasBeenShownById(broadcastId: number): boolean {
    return this.shownBroadcastVersions.has(broadcastId);
  }

  private isValidJson(str: string): boolean {
    try {
      JSON.parse(str);
      return true;
    } catch {
      return false;
    }
  }

  setCurrentOrganization(organizationId: number | null): void {
    this.currentOrganizationId = organizationId;
  }

  getCurrentOrganization(): number | null {
    return this.currentOrganizationId;
  }
}
